import {
  Box,
  Button,
  Checkbox,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { projectSlice } from '../../../redux/ProjectSlice';
import type { RootState } from '../../../redux/Store';
import {
  createDefaultFieldSelection,
  formatFieldLabel,
  FIELD_FAMILY_LABEL_DEFAULT,
  getFieldFamilyLabelDescriptor,
  getFieldGeometry,
  getFieldSvgDataUri,
  getOrderedFieldFamilies,
  getFieldOptionsForFamily,
  getFieldVariantLabelDefault,
  getFieldVariantLabelDescriptor,
  getFieldVariantLabel,
  normalizeFieldSelection,
  type FieldOption,
  type FieldSelection,
} from '../../../utils/fieldSelection';

interface FieldSelectionDialogProps {
  open: boolean;
  onClose: () => void;
}

const buildPreviewImageSrc = (fieldSelection: FieldSelection) => getFieldSvgDataUri(fieldSelection);

const getPreviewDimensions = ({ widthCm, heightCm }: ReturnType<typeof getFieldGeometry>) => {
  const maxWidth = 560;
  const maxHeight = 420;
  const aspectRatio = widthCm / heightCm;

  if (aspectRatio >= 1) {
    const width = Math.min(maxWidth, maxHeight * aspectRatio);
    return {
      width: Math.round(width),
      height: Math.round(width / aspectRatio),
    };
  }

  const height = Math.min(maxHeight, maxWidth / aspectRatio);
  return {
    width: Math.round(height * aspectRatio),
    height: Math.round(height),
  };
};

type FieldSelectionOptionRowProps = {
  onSelect: (selection: Pick<FieldSelection, 'family' | 'variant'>) => void;
  option: FieldOption;
  selected: boolean;
};

const FieldSelectionOptionRow = ({ option, selected, onSelect }: FieldSelectionOptionRowProps) => {
  const { t } = useTranslation();
  const primaryLabel = t(getFieldVariantLabel(option), getFieldVariantLabelDefault(option));

  return (
    <ListItemButton
      selected={selected}
      role="option"
      aria-selected={selected}
      data-testid={`field-selection-option-${option.family}-${option.variant}`}
      onClick={() =>
        onSelect({
          family: option.family,
          variant: option.variant,
        })
      }
      sx={{
        borderRadius: 1.5,
        mb: 0.5,
        px: 1.5,
        py: 0.75,
        color: 'text.secondary',
        '&.Mui-selected': {
          bgcolor: 'action.selected',
          color: 'text.primary',
          boxShadow: (theme) => `inset 0 0 0 1px ${theme.palette.divider}`,
        },
        '&.Mui-selected:hover': {
          bgcolor: 'action.selected',
        },
      }}
    >
      <ListItemText
        primary={primaryLabel}
        primaryTypographyProps={{
          variant: 'body2',
          fontWeight: selected ? 700 : 500,
        }}
      />
    </ListItemButton>
  );
};

const FieldSelectionDialog = ({ open, onClose }: FieldSelectionDialogProps) => {
  const { loadedProject } = useSelector((state: RootState) => state.project);
  const persistedFieldSelection = useMemo(
    () => (loadedProject?.selectedField ? normalizeFieldSelection(loadedProject.selectedField) : createDefaultFieldSelection()),
    [loadedProject?.selectedField]
  );
  const [fieldSelection, setFieldSelection] = useState<FieldSelection>(persistedFieldSelection);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      setFieldSelection(persistedFieldSelection);
    }
  }, [open, persistedFieldSelection]);

  const dialogHeading = useMemo(() => {
    const familyLabel = getFieldFamilyLabelDescriptor(fieldSelection.family);
    const variantLabel = getFieldVariantLabelDescriptor(fieldSelection);

    return formatFieldLabel(t(familyLabel.key, familyLabel.defaultValue), t(variantLabel.key, variantLabel.defaultValue));
  }, [fieldSelection, t]);
  const fieldGeometry = getFieldGeometry(fieldSelection);
  const fieldSvgPreviewSrc = buildPreviewImageSrc(fieldSelection);
  const previewDimensions = getPreviewDimensions(fieldGeometry);
  const orderedFamilies = getOrderedFieldFamilies();

  const handleSelectionChange = (nextSelection: Pick<FieldSelection, 'family' | 'variant'>) => {
    setFieldSelection((prev) => ({
      ...nextSelection,
      accessoriesEnabled: prev.accessoriesEnabled,
    }));
  };

  const handleAccessoriesToggle = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setFieldSelection((prev) => ({ ...prev, accessoriesEnabled: !checked }));
  };

  const handleConfirm = () => {
    dispatch(projectSlice.actions.onUpdateSelectedField(fieldSelection));
    onClose();
  };

  return (
    <>
      <DialogTitle textAlign="center">
        <Trans i18nKey="fieldSelectionDialog.title">Select Field</Trans>
      </DialogTitle>
      <Divider />
      <DialogContent
        sx={{
          width: {
            xs: 'min(100vw - 32px, 960px)',
            md: 960,
          },
          maxWidth: '100%',
          overflowY: 'visible',
          px: 3,
          pt: 3,
          pb: 3,
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={3}
          sx={{
            minHeight: { md: 560 },
          }}
        >
          <Box
            sx={{
              width: { xs: '100%', md: 280 },
              flexShrink: 0,
              borderRight: { md: 1 },
              borderColor: { md: 'divider' },
              pr: { md: 2 },
            }}
          >
            <List disablePadding role="listbox" aria-label={t('fieldSelectionDialog.catalog', 'Field catalog')} data-testid="field-selection-sidebar">
              {orderedFamilies.map((family, familyIndex) => (
                <Box key={family.value} sx={{ mb: familyIndex === orderedFamilies.length - 1 ? 0 : 2 }}>
                  <Typography
                    variant="caption"
                    color="text.primary"
                    sx={{
                      px: 1.5,
                      pb: 0.75,
                      textTransform: 'uppercase',
                      fontWeight: 800,
                      letterSpacing: 0.8,
                    }}
                  >
                    {t(family.label, FIELD_FAMILY_LABEL_DEFAULT[family.value])}
                  </Typography>
                  {getFieldOptionsForFamily(family.value).map((option) => (
                    <FieldSelectionOptionRow
                      key={`${option.family}-${option.variant}`}
                      option={option}
                      selected={fieldSelection.family === option.family && fieldSelection.variant === option.variant}
                      onSelect={handleSelectionChange}
                    />
                  ))}
                </Box>
              ))}
            </List>
          </Box>

          <Stack spacing={2.5} sx={{ flex: 1, minWidth: 0 }}>
            <Stack spacing={0.5}>
              <Typography variant="h5" data-testid="field-selection-preview-title">
                {dialogHeading}
              </Typography>
            </Stack>

            <Paper
              data-testid="field-selection-preview-shell"
              variant="outlined"
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                bgcolor: 'background.default',
              }}
            >
              <Box
                data-testid="field-selection-preview-frame"
                sx={{
                  width: '100%',
                  minHeight: 420,
                  maxWidth: 560,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={fieldSvgPreviewSrc}
                  alt={dialogHeading}
                  data-testid="field-selection-preview-image"
                  sx={{
                    width: `${previewDimensions.width}px`,
                    height: `${previewDimensions.height}px`,
                    maxWidth: '100%',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              </Box>
            </Paper>

            <Box>
              <FormControlLabel
                control={<Checkbox checked={!fieldSelection.accessoriesEnabled} onChange={handleAccessoriesToggle} />}
                label={t('fieldSelectionDialog.accessoriesToggle', 'Hide field objects')}
              />
            </Box>
          </Stack>
        </Stack>
      </DialogContent>
      <Divider />
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} px={3} py={2}>
        <Button variant="text" onClick={onClose}>
          <Trans i18nKey="fieldSelectionDialog.button.close">Close</Trans>
        </Button>
        <Button variant="contained" onClick={handleConfirm}>
          <Trans i18nKey="fieldSelectionDialog.button.confirm">Confirm</Trans>
        </Button>
      </Stack>
    </>
  );
};

export default FieldSelectionDialog;
